import { Wallet, RefreshCw, Monitor, Smartphone, Tablet, Trash2 } from 'lucide-react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { dashboardApi } from '@/lib/api/dashboard'
import { devicesApi } from '@/lib/api/devices'
import type { TrustedDevice } from '@portal/types'

function DeviceIcon({ type }: { type: string }) {
  if (type === 'MOBILE') return <Smartphone className="h-4 w-4" />
  if (type === 'TABLET') return <Tablet className="h-4 w-4" />
  return <Monitor className="h-4 w-4" />
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  APPROVED: { label: 'Trusted', className: 'bg-emerald-100 text-emerald-700' },
  PENDING: { label: 'Pending Approval', className: 'bg-amber-100 text-amber-700' },
  REVOKED: { label: 'Revoked', className: 'bg-slate-100 text-slate-500' },
  REJECTED: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
}

export default function AccountPage() {
  const qc = useQueryClient()

  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ['my-devices'],
    queryFn: () => devicesApi.getMyDevices(),
    select: (res) => res.data ?? [],
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => devicesApi.revokeMyDevice(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-devices'] }),
  })

  const { data: balance, isLoading } = useQuery({
    queryKey: ['balance'],
    queryFn: () => dashboardApi.balance(),
    select: (res) => res.data,
    staleTime: 120_000,
  })

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">ICICI Corporate Account</CardTitle>
          <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ['balance'] })}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
              <Wallet className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Available Balance</p>
              {isLoading ? (
                <Skeleton className="mt-1 h-7 w-36" />
              ) : balance?.error ? (
                <p className="text-base font-medium text-muted-foreground">Unavailable — {balance.error}</p>
              ) : (
                <p className="text-2xl font-bold">
                  {balance?.balancePaise != null
                    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(balance.balancePaise / 100)
                    : '—'}
                </p>
              )}
              {balance?.lastChecked && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Last checked {new Date(balance.lastChecked).toLocaleTimeString('en-IN')}
                </p>
              )}
            </div>
          </div>

          {balance?.accountNo && (
            <div className="rounded-lg border bg-muted/30 px-4 py-3">
              <p className="text-xs text-muted-foreground">Account Number</p>
              <p className="mt-0.5 font-mono text-sm font-medium">
                {'X'.repeat(Math.max(0, (balance.accountNo?.length ?? 0) - 4)) + (balance.accountNo?.slice(-4) ?? '')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-muted-foreground">
            {['Bank Statement download', 'Transaction limits management', 'Beneficiary management', 'NEFT/RTGS/IMPS scheduling'].map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                <span>{feature}</span>
                <span className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[10px]">Coming soon</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Trusted Devices</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => qc.invalidateQueries({ queryKey: ['my-devices'] })}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </CardHeader>
        <CardContent>
          {devicesLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : !devices?.length ? (
            <p className="text-sm text-muted-foreground">No devices registered.</p>
          ) : (
            <div className="space-y-2">
              {(devices as TrustedDevice[]).map((device) => {
                const badge = STATUS_BADGE[device.status] ?? { label: device.status, className: 'bg-slate-100 text-slate-500' }
                return (
                  <div key={device.id} className="flex items-center gap-3 rounded-lg border px-4 py-3">
                    <div className="text-muted-foreground">
                      <DeviceIcon type={device.deviceType} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{device.deviceName}</p>
                      <p className="text-xs text-muted-foreground">
                        {device.ipAddress ? `${device.ipAddress} · ` : ''}
                        Added {new Date(device.createdAt).toLocaleDateString('en-IN')}
                        {device.lastUsedAt ? ` · Last used ${new Date(device.lastUsedAt).toLocaleDateString('en-IN')}` : ''}
                      </p>
                    </div>
                    <Badge className={`text-xs ${badge.className} border-0`}>
                      {badge.label}
                    </Badge>
                    {device.status === 'APPROVED' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        disabled={revokeMutation.isPending}
                        onClick={() => revokeMutation.mutate(device.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
